import { useEffect, useRef, useState } from 'react';
import type { PublicUser } from '@otter/shared';
import { canSeeOthers } from '../lib/perm';
import { ChevronDownIcon, LogoutIcon, MenuIcon } from './Icons';
import { ConfirmDialog } from './ConfirmDialog';

type Props = {
  user: PublicUser;
  crumb: string;
  scope: string;
  onScope: (s: string) => void;
  comerciales: Array<{ code: string; name: string }>;
  onLogout: () => void;
  onToggleNav: () => void;
};

export function Topbar({ user, crumb, scope, onScope, comerciales, onLogout, onToggleNav }: Props) {
  const showScope = canSeeOthers(user) && comerciales.length > 0;
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmingLogout, setConfirmingLogout] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    function onDocClick(e: MouseEvent) {
      if (!wrapRef.current) return;
      if (e.target instanceof Node && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setMenuOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <div className="topbar">
      <button
        type="button"
        className="nav-toggle"
        onClick={onToggleNav}
        aria-label="Abrir menú"
      >
        <MenuIcon />
      </button>
      <div className="crumb">
        ~/otter/<b>{crumb}</b>
      </div>
      {showScope && (
        <div className="scope">
          <button className={scope === 'all' ? 'on' : ''} onClick={() => onScope('all')}>
            todos
          </button>
          {comerciales.map((c) => (
            <button
              key={c.code}
              className={scope === c.code ? 'on' : ''}
              onClick={() => onScope(c.code)}
              title={c.name}
            >
              {c.code}
            </button>
          ))}
        </div>
      )}
      <div className="topright">
        <div className="stat">
          <span className="pulse" /> live
        </div>
        <div className="who-wrap" ref={wrapRef}>
          <button
            type="button"
            className={`who-pill who-pill-trigger${menuOpen ? ' open' : ''}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <span className="av">{user.code}</span>
            <div className="who-name">
              <b>{user.name}</b>
              <div className="role">{user.role.name}</div>
            </div>
            <span className="who-caret">
              <ChevronDownIcon />
            </span>
          </button>
          {menuOpen && (
            <div className="who-menu" role="menu">
              <div className="who-menu-head">
                <b>{user.name}</b>
                <span className="email">{user.email}</span>
              </div>
              <button
                type="button"
                role="menuitem"
                className="who-menu-item danger"
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmingLogout(true);
                }}
              >
                <LogoutIcon /> Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
      {confirmingLogout && (
        <ConfirmDialog
          tone="danger"
          title="Cerrar sesión"
          message="Se cerrará tu sesión y volverás a la pantalla de login."
          confirmLabel="Cerrar sesión"
          onCancel={() => setConfirmingLogout(false)}
          onConfirm={() => {
            setConfirmingLogout(false);
            onLogout();
          }}
        />
      )}
    </div>
  );
}
